import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'motion/react';
import { cn } from '@/src/infrastructure/lib/utils';

export interface CorridorProps {
  children: React.ReactNode;
  className?: string;
  depthScale?: number;
}

export function Corridor({ children, className, depthScale = 1.15 }: CorridorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const shouldReduceMotion = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });

  // Smooth corridor walk: camera/scene zooms inward along Z-axis while advancing
  const scale = useTransform(scrollYProgress, [0, 1], [1.0, depthScale]);
  const opacity = useTransform(scrollYProgress, [0, 0.25, 0.75, 1], [0.5, 1, 1, 0.5]);

  return (
    <div
      ref={containerRef}
      className={cn('relative w-full h-full overflow-hidden isolate z-0', className)}
    >
      <motion.div
        style={{
          scale: shouldReduceMotion ? 1 : scale,
          opacity: shouldReduceMotion ? 1 : opacity,
        }}
        className="w-full h-full absolute inset-0 pointer-events-none"
      >
        {/* Subtle corridor side pillar vignette gradients */}
        <div
          className="absolute inset-0 z-10 pointer-events-none"
          style={{
            background:
              'linear-gradient(90deg, rgba(0,0,0,0.6) 0%, transparent 20%, transparent 80%, rgba(0,0,0,0.6) 100%)',
          }}
          aria-hidden
        />
      </motion.div>

      <div className="relative z-20 w-full h-full flex items-center justify-center">
        {children}
      </div>
    </div>
  );
}
